import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

/**
 * Compact per-panel error boundary (UX-audit item 2): one bad record must
 * blank ONE panel, not the whole workspace. The fallback is an inline card
 * with a Retry that re-mounts just this subtree (key bump), so the operator
 * keeps the rest of the screen and their context.
 */
class PanelBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, epoch: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps) {
    // New content identity (tab switch, different quote) gets a fresh chance —
    // otherwise a crash in one tab stays stuck on screen for every other tab.
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState((s) => ({ hasError: false, epoch: s.epoch + 1 }));
    }
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(`[PanelBoundary:${this.props.label || "panel"}]`, error, info);
  }

  retry = () => {
    this.setState((s) => ({ hasError: false, epoch: s.epoch + 1 }));
  };

  render() {
    if (this.state.hasError) {
      // silent mode: for floating chrome (FAB, overlays) where an inline
      // fallback card would look stranded — fail invisible, never fatal.
      if (this.props.silent) return null;
      return (
        <div className="flex h-full min-h-[160px] w-full flex-col items-center justify-center gap-3 rounded-2xl border border-amber-200/70 bg-amber-50/60 p-6 text-center dark:border-amber-500/25 dark:bg-amber-500/[0.06]">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300">
            <FontAwesomeIcon icon={faTriangleExclamation} className="text-[14px]" />
          </span>
          <div>
            <p className="text-[13px] font-semibold text-slate-800 dark:text-slate-100">
              Couldn't load {this.props.label || "this panel"}
            </p>
            <p className="mt-0.5 text-[12px] text-slate-500 dark:text-slate-400">
              The rest of the workspace is unaffected.
            </p>
          </div>
          <button
            type="button"
            onClick={this.retry}
            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300/70 bg-white px-3 py-1.5 text-[12px] font-semibold text-amber-700 transition hover:bg-amber-50 dark:border-amber-500/30 dark:bg-[oklch(0.225_0.026_264)] dark:text-amber-300 dark:hover:bg-[oklch(0.26_0.028_264)]"
          >
            <FontAwesomeIcon icon={faRotateRight} className="text-[10px]" />
            Retry
          </button>
        </div>
      );
    }
    // key bump re-mounts children fresh on retry
    return <React.Fragment key={this.state.epoch}>{this.props.children}</React.Fragment>;
  }
}

export default PanelBoundary;
